import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Button, Form, Alert, Table, Modal } from 'react-bootstrap';
import FilterComponent, { FilterOption, AppliedFilter } from '../../../components/common/FilterComponent';
import DeleteConfirmationModal from '../../../components/common/DeleteConfirmationModal';
import { itemBaseService, Item } from '../../../services/itemBaseService';
import { questionBaseService, Question } from '../../../services/questionBaseService';
import { areaService, Area } from '../../../services/areaService';

interface NewItem {
    title: string;
    description: string;
}

const QuestionItemsPage: React.FC = () => {
    const { questionId } = useParams<{ questionId: string }>();
    const history = useHistory();
    const [question, setQuestion] = useState<Question | null>(null);
    const [items, setItems] = useState<Item[]>([]);
    const [areas, setAreas] = useState<Area[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [showNewItemModal, setShowNewItemModal] = useState<boolean>(false);
    const [showEditItemModal, setShowEditItemModal] = useState<boolean>(false);
    const [selectedItem, setSelectedItem] = useState<Item | null>(null);
    const [formData, setFormData] = useState<NewItem>({ title: '', description: '' });
    const [showFilter, setShowFilter] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [filterType, setFilterType] = useState<'item'>('item');
    const [appliedFilters, setAppliedFilters] = useState<AppliedFilter[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [filterLoading, setFilterLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [selectedItemForDelete, setSelectedItemForDelete] = useState<Item | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const filterOptions: FilterOption[] = [
        { value: 'item', label: 'Item' }
    ];

    const fetchItems = async (filters?: { titulo?: string }) => {
        try {
            setFilterLoading(true);
            setError(null);

            const itemsResponse = await itemBaseService.getItems({
                page: 0,
                size: 100, // Get a reasonable number of items
                questaoBaseId: questionId,
                ...filters
            });
            setItems(itemsResponse.content);
        } catch (err) {
            setError('Erro ao carregar itens. Por favor, tente novamente.');
            console.error('Error fetching items:', err);
        } finally {
            setFilterLoading(false);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                // Fetch question details
                const questionData = await questionBaseService.getQuestionById(questionId);
                setQuestion(questionData);

                // Fetch areas
                const areasData = await areaService.getAreas();
                setAreas(areasData);

                // Fetch items for this question
                await fetchItems();

            } catch (err) {
                setError('Erro ao carregar dados. Por favor, tente novamente.');
                console.error('Error fetching data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [questionId]);

    const handleNewItemChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAddNewItem = async () => {
        if (formData.title.trim() && formData.description.trim() && question) {
            try {
                setSubmitting(true);
                const newItem = await itemBaseService.createItem({
                    titulo: formData.title.trim(),
                    descricao: formData.description.trim(),
                    tipoBase: 'KATRU',
                    questaoBaseId: questionId,
                    acaoIds: []
                });

                setItems(prev => [...prev, newItem]);
                setFormData({ title: '', description: '' });
                setShowNewItemModal(false);
            } catch (err) {
                setError('Erro ao criar item. Por favor, tente novamente.');
                console.error('Error creating item:', err);
            } finally {
                setSubmitting(false);
            }
        }
    };

    const handleEditClick = (item: Item) => {
        setSelectedItem(item);
        setFormData({
            title: item.titulo,
            description: item.descricao
        });
        setShowEditItemModal(true);
    };

    const handleUpdateItem = async () => {
        if (selectedItem && formData.title.trim() && formData.description.trim()) {
            try {
                setSubmitting(true);
                const updatedItem = await itemBaseService.updateItem(selectedItem.itemBaseId!, {
                    titulo: formData.title.trim(),
                    descricao: formData.description.trim(),
                    tipoBase: 'KATRU',
                    questaoBaseId: questionId,
                    acaoIds: selectedItem.acaoIds || []
                });

                setItems(prevItems =>
                    prevItems.map(item =>
                        item.itemBaseId === selectedItem.itemBaseId
                            ? updatedItem
                            : item
                    )
                );
                setShowEditItemModal(false);
                setSelectedItem(null);
                setFormData({ title: '', description: '' });
            } catch (err) {
                setError('Erro ao atualizar item. Por favor, tente novamente.');
                console.error('Error updating item:', err);
            } finally {
                setSubmitting(false);
            }
        }
    };

    const handleModalClose = () => {
        setShowNewItemModal(false);
        setShowEditItemModal(false);
        setSelectedItem(null);
        setFormData({ title: '', description: '' });
    };

    const handleDeleteClick = (item: Item) => {
        setSelectedItemForDelete(item);
        setShowDeleteModal(true);
    };

    const handleDeleteModalClose = () => {
        setShowDeleteModal(false);
        setSelectedItemForDelete(null);
        setIsDeleting(false);
    };

    const handleConfirmDelete = async () => {
        if (!selectedItemForDelete) return;

        try {
            setIsDeleting(true);
            await itemBaseService.deleteItem(selectedItemForDelete.itemBaseId!);
            setItems(prev => prev.filter(item => item.itemBaseId !== selectedItemForDelete.itemBaseId));
            handleDeleteModalClose();
        } catch (err) {
            setError('Erro ao remover item. Por favor, tente novamente.');
            console.error('Error deleting item:', err);
        } finally {
            setIsDeleting(false);
        }
    };

    const getAreaName = (areaId: string) => {
        return areas.find(area => area.area_id === areaId)?.nome || 'Área não encontrada';
    };

    // Function to apply filters (called when user clicks search or presses Enter)
    const handleFilterApply = async (filterText: string, filterType: string) => {
        const typedFilterType = filterType as 'item';

        // Only apply if filter text is not empty
        if (!filterText.trim()) return;

        // Add to applied filters
        const filterOption = filterOptions.find(opt => opt.value === typedFilterType);
        const newAppliedFilter: AppliedFilter = {
            type: typedFilterType,
            value: filterText.trim(),
            label: filterOption?.label || typedFilterType
        };

        // Remove existing filter of same type if exists
        const updatedFilters = appliedFilters.filter(f => f.type !== typedFilterType);
        setAppliedFilters([...updatedFilters, newAppliedFilter]);

        // Clear the input after applying
        setFilterText('');

        // Apply the filter to backend
        await applyFiltersToBackend([...updatedFilters, newAppliedFilter]);
    };

    // Function to remove a specific filter
    const handleFilterRemove = async (filterType: string) => {
        const updatedFilters = appliedFilters.filter(f => f.type !== filterType);
        setAppliedFilters(updatedFilters);
        await applyFiltersToBackend(updatedFilters);
    };

    // Function to apply filters to backend
    const applyFiltersToBackend = async (filters: AppliedFilter[]) => {
        // Prepare filters for backend
        const backendFilters: { titulo?: string } = {};

        if (filters.length > 0) {
            filters.forEach(filter => {
                if (filter.type === 'item') {
                    backendFilters.titulo = filter.value;
                }
            });
        }

        // Fetch filtered data from backend
        await fetchItems(backendFilters);
    };

    // Function to clear filters
    const handleFilterClear = async () => {
        setFilterText('');
        setAppliedFilters([]);
        // Fetch all data without filters
        await fetchItems();
    };

    const handleBack = () => {
        history.push('/admin/questoes');
    };

    const handleActionsClick = (itemId: string) => {
        history.push(`/admin/questoes/${questionId}/itens/${itemId}/acoes`);
    };

    // No need for client-side filtering since we're fetching filtered data from backend
    const displayItems = items;

    if (loading) {
        return <div className="text-center p-5">Carregando...</div>;
    }

    if (error || !question) {
        return (
            <div className="container-fluid p-4">
                <Alert variant="danger">
                    {error || 'Questão não encontrada'}
                </Alert>
                <Button variant="primary" onClick={handleBack}>
                    Voltar para Questões
                </Button>
            </div>
        );
    }

    return (
        <div className="container-fluid p-4">
            <Button
                variant="outline-secondary"
                onClick={() => history.push('/admin/questoes')}
                className="return-button mb-3 d-flex align-items-center gap-2"
            >
                <span>←</span> Voltar
            </Button>
            <div className="dashboard-header mb-4 d-flex flex-row justify-content-between">
                <h2 className="mb-0 text-start">Gerenciar Itens da Questão</h2>
                <Button variant="primary" onClick={() => setShowNewItemModal(true)}>
                    Adicionar Item
                </Button>
            </div>

            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <p className="text-muted mb-2">
                        Área: {getAreaName(question.areaId)}
                    </p>
                    <p className="text-muted mb-2">
                        Questão: {question.titulo}
                    </p>
                </div>
            </div>

            {!showFilter && (
                <div className="mb-2">
                    <Button
                        variant="outline-secondary"
                        onClick={() => setShowFilter(true)}
                        className="filter-toggle-button d-inline-flex align-items-center gap-2"
                        aria-expanded={showFilter}
                    >
                        <span>🔍</span>
                        Filtrar
                    </Button>
                </div>
            )}
            {showFilter && (
                <div className="mb-3">
                    <FilterComponent
                        showFilter={showFilter}
                        filterText={filterText}
                        filterType={filterType}
                        filterOptions={filterOptions}
                        appliedFilters={appliedFilters}
                        onFilterApply={handleFilterApply}
                        onFilterTypeChange={(type) => setFilterType(type as 'item')}
                        onFilterTextChange={setFilterText}
                        onFilterTextClear={handleFilterClear}
                        onFilterToggle={() => setShowFilter(false)}
                        onFilterRemove={handleFilterRemove}
                        placeholderText={`Buscar por ${filterType === 'item' ? 'item' : 'ações'}...`}
                    />
                </div>
            )}

            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            )}

            {filterLoading && (
                <div className="text-center p-3">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                    </div>
                </div>
            )}

            <div className="table-responsive">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>Nome</th>
                            <th>Descrição</th>
                            <th style={{ width: '300px' }}>Gerenciar</th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayItems.map(item => (
                            <tr key={item.itemBaseId || `item-${item.titulo}`}>
                                <td>{item.titulo}</td>
                                <td>{item.descricao}</td>
                                <td>
                                    <div className="d-flex gap-2 justify-content-end">
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => handleEditClick(item)}
                                        >
                                            Editar
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            className="d-flex align-items-center border border-secondary text-secondary"
                                            size="sm"
                                            onClick={() => handleActionsClick(item.itemBaseId!)}
                                        >
                                            Gerenciar Ações
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => handleDeleteClick(item)}
                                        >
                                            Excluir
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>

            {items.length === 0 && (
                <div className="text-center text-muted p-4">
                    Nenhum item configurado para esta questão.
                </div>
            )}

            {/* Add Item Modal */}
            <Modal show={showNewItemModal} onHide={handleModalClose}>
                <Modal.Header>
                    <Modal.Title>Adicionar Item</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Nome</Form.Label>
                        <Form.Control
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleNewItemChange}
                            placeholder="Digite o nome do item"
                            required
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Descrição</Form.Label>
                        <Form.Control
                            as="textarea"
                            name="description"
                            value={formData.description}
                            onChange={handleNewItemChange}
                            rows={3}
                            placeholder="Digite a descrição do item"
                            required
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleModalClose}>
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleAddNewItem}
                        disabled={!formData.title.trim() || !formData.description.trim() || submitting}
                    >
                        {submitting ? 'Adicionando...' : 'Adicionar Item'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Edit Item Modal */}
            <Modal show={showEditItemModal} onHide={handleModalClose}>
                <Modal.Header>
                    <Modal.Title>Editar Item</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Nome</Form.Label>
                        <Form.Control
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleNewItemChange}
                            placeholder="Digite o nome do item"
                            required
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Descrição</Form.Label>
                        <Form.Control
                            as="textarea"
                            name="description"
                            value={formData.description}
                            onChange={handleNewItemChange}
                            rows={3}
                            placeholder="Digite a descrição do item"
                            required
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleModalClose}>
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleUpdateItem}
                        disabled={!formData.title.trim() || !formData.description.trim() || submitting}
                    >
                        {submitting ? 'Salvando...' : 'Salvar Alterações'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
                show={showDeleteModal}
                onHide={handleDeleteModalClose}
                onConfirm={handleConfirmDelete}
                title="Confirmar Exclusão"
                message={`Tem certeza que deseja excluir o item "${selectedItemForDelete?.titulo}"?`}
                warningMessage="Esta ação irá remover todas as ações associadas a este item."
                entityName="item"
                isDeleting={isDeleting}
            />
        </div>
    );
};

export default QuestionItemsPage; 